import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { FacilityDocumentsService } from 'src/app/shared/services/facility-documents.service';
import { FacilityDocumentStatus } from 'src/app/shared/services/facility-documents.service';
import { FeatureFlagsService } from 'src/app/shared/services/featureFlags.service';
import { PastDueService } from 'src/app/shared/services/pastDue.service';
import { UserService } from 'src/app/shared/services/user/user.service';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { paymentOptionLabel } from 'src/app/shared/helpers/payment-option';
import { emailStatusOf } from 'src/app/shared/models/user';

@Component({
  selector: 'app-facility-card',
  templateUrl: './facility-card.component.html',
  styleUrls: ['./facility-card.component.scss'],
})
export class FacilityCardComponent implements OnInit {
  @Input() facility: any;
  @Input() primaryFacilityId: any;
  @Input() userId: any;
  @Input() isReadOnly: boolean = false;
  @Output() switch = new EventEmitter<any>();

  readonly emailStatusOf = emailStatusOf;

  status: FacilityDocumentStatus;
  showUsersPopup: boolean = false;
  EXTERNAL_VETTING_COMPANY_NAME = 'Caretria';

  termsToolsEnabled = false;
  hardGateActive = false;
  emailGateOn = false;
  pendingSwitch = false;
  newUser: any = {};
  showAddUserForm = false;
  pending = false;

  get termsPendingTooltip(): string {
    const state = this.hardGateActive
      ? 'Customer CANNOT place orders until the Terms & Conditions are accepted.'
      : 'Customer not agreed to terms / activated their portal account.';
    return this.termsToolsEnabled
      ? `${state} Click to view the users and select who to send the terms link to.`
      : state;
  }

  get showActionHint(): boolean {
    return this.termsToolsEnabled
      && (this.facility?.user_count === 0 || !!this.facility?.require_terms_agreement);
  }

  openAddUser() {
    this.showAddUserForm = true;
  }

  get paymentTypeLabel(): string {
    return paymentOptionLabel(this.facility?.payment_option);
  }

  // Credit-card companies pay at checkout, so they have no credit position to show.
  get isTermsCompany(): boolean {
    return this.facility?.payment_option !== 'credit_card';
  }

  get usersPopupTitle(): string {
    return this.termsToolsEnabled && this.facility?.require_terms_agreement
      ? 'Send Terms & Conditions Link — ' + this.facility.name
      : 'Users - ' + this.facility?.name;
  }

  get zeroUsersTooltip(): string {
    return this.termsToolsEnabled
      ? 'There are no users set up on this account. Click to add a user.'
      : "There are no users set up on this account. Please find email addresses of the account principals and contact <a href='mailto:customersupport@letybousa.com'>customersupport@letybousa.com</a> with this information so we can complete the account setup.";
  }

  constructor(
    private facilityDocumentsService: FacilityDocumentsService,
    public featureFlagsService: FeatureFlagsService,
    public pastDue: PastDueService,
    private userService: UserService,
    private notificationService: NotificationService,
  ) { }

  ngOnInit() {
    this.status = this.facilityDocumentsService.evaluateStatus(this.facility, [], true);
    this.hardGateActive = !!this.featureFlagsService.getByEnv('process', 'terms_acceptance_gate');
    this.termsToolsEnabled = this.hardGateActive ||
      !!this.featureFlagsService.getByEnv('process', 'soft_terms_acceptance_gate');
    this.emailGateOn = !!this.featureFlagsService.getByEnv('process', 'email_confirmation_gate');
  }

  sendTermsLink(user: any) {
    this.pending = true;
    this.userService.sendTermsLink(this.facility.id, user.id).subscribe({
      next: () => {
        this.notificationService.showSuccessMessage(`Terms link sent to ${user.email}`);
        if (this.pendingSwitch) {
          this.showUsersPopup = false; // onHidden continues the facility selection
        }
      },
      error: e => this.notificationService.showErrorMessage(e?.error?.error || 'Failed to send'),
    }).add(() => this.pending = false);
  }

  verifyEmail(user: any) {
    this.pending = true;
    this.userService.verifyEmail(user).finally(() => (this.pending = false));
  }

  addUser() {
    this.pending = true;
    this.userService.repCreateUser(this.facility.id, this.newUser).subscribe({
      next: r => {
        this.facility.users = [...(this.facility.users || []),
          { id: r.data.id, name: `${this.newUser.first_name} ${this.newUser.last_name || ''}`.trim(),
            email: this.newUser.email, phone: this.newUser.phone }];
        this.showAddUserForm = false; this.newUser = {};
      },
      error: e => this.notificationService.showErrorMessage(e?.error?.error || 'Failed to create user'),
    }).add(() => this.pending = false);
  }

  get hasStateLicenseIssue(): boolean {
    return Boolean(this.status?.state_license_missing || this.status?.state_license_expired);
  }

  get hasDeaIssue(): boolean {
    return Boolean(this.status?.dea_license_missing || this.status?.dea_license_expired);
  }

  get stateLicenseExpiringSoon(): boolean {
    if (this.hasStateLicenseIssue) {
      return false;
    }

    const expirationDate = this.parseDate(this.facility?.state_license_expiration);
    if (!expirationDate) {
      return false;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const oneMonthFromNow = new Date(today);
    oneMonthFromNow.setDate(oneMonthFromNow.getDate() + 30);

    return expirationDate >= today && expirationDate <= oneMonthFromNow;
  }

  get showLicenseAlert(): boolean {
    return this.hasStateLicenseIssue || this.stateLicenseExpiringSoon || this.hasDeaIssue;
  }

  get licenseAlertClass(): string {
    return this.hasStateLicenseIssue || this.hasDeaIssue
      ? 'state-license-danger-icon'
      : 'state-license-warning-icon';
  }

  get licenseAlertTooltipText(): string {
    const messages: string[] = [];

    if (this.status?.state_license_missing) {
      messages.push("This facility's state license is missing.");
    }

    if (this.status?.state_license_expired) {
      messages.push("This facility's state license is expired.");
    }

    if (this.stateLicenseExpiringSoon) {
      messages.push("This facility's state license is expiring within 30 days.");
    }

    if (this.status?.dea_license_missing) {
      messages.push("This facility's DEA license is missing.");
    }

    if (this.status?.dea_license_expired) {
      messages.push("This facility's DEA license is expired.");
    }

    return messages.join('<br/>');
  }

  get unvettedTooltipText(): string {
    const externalVettingStatus = (this.facility?.external_vetting || '').toLowerCase();
    if (externalVettingStatus === 'new') {
      return 'Facility created, our team will review the facility and documents within 1-2 business days.';
    }

    if (externalVettingStatus === 'pending') {
      return `Facility has been sent to ${this.EXTERNAL_VETTING_COMPANY_NAME}, waiting on account setup completion`;
    }

    if (externalVettingStatus) {
      return `External vetting status: ${this.facility.external_vetting}`;
    }

    return 'This facility is not yet vetted. This normally applies to newly registered accounts. Vetting normally takes 1-2 business days assuming valid account information and license.';
  }

  private parseDate(dateValue: string): Date | null {
    if (!dateValue) {
      return null;
    }

    const parsedDate = new Date(dateValue);
    if (!Number.isNaN(parsedDate.getTime())) {
      parsedDate.setHours(0, 0, 0, 0);
      return parsedDate;
    }

    const dateParts = dateValue.split('/');
    if (dateParts.length !== 3) {
      return null;
    }

    const [month, day, year] = dateParts.map(part => Number(part));
    if (!month || !day || !year) {
      return null;
    }

    const normalizedDate = new Date(year, month - 1, day);
    normalizedDate.setHours(0, 0, 0, 0);
    return Number.isNaN(normalizedDate.getTime()) ? null : normalizedDate;
  }

  // Soft-block week only: selecting a terms-pending facility detours through
  // the users popup (send the link / add a user) before the switch proceeds.
  get softInterceptActive(): boolean {
    return this.termsToolsEnabled && !this.hardGateActive
      && !!this.facility?.require_terms_agreement;
  }

  onSwitch() {
    if (this.softInterceptActive && !this.pendingSwitch) {
      this.pendingSwitch = true;
      this.showUsersPopup = true;
      return;
    }
    this.switch.emit({ facility_id: this.facility.id, user_id: this.userId });
  }

  // Every exit from the intercept popup — link sent, Cancel, X, outside
  // click — continues to the facility selection.
  proceedPendingSwitch() {
    if (this.pendingSwitch) {
      this.pendingSwitch = false;
      this.switch.emit({ facility_id: this.facility.id, user_id: this.userId });
    }
  }
}
